/**
 * i18n. Spec 2.3 ("Internationalisation"), spec 4.4's locale file layout, spec 23.2.
 *
 * Locale is a path prefix (`/:lang/...`), so the router is the source of truth and this
 * module follows it. The stored preference exists only to choose where to send someone who
 * arrives at `/`.
 *
 * ── Why the other three locales are lazy ────────────────────────────────────
 * Spec 23.2: "locale bundles split so a German user never downloads Russian strings."
 * They used to be static imports, all four of them, and the cost was measured rather than
 * assumed: 13,522 Cyrillic characters in the entry chunk of every page load, including the
 * landing page of a German visitor who will never see one of them.
 *
 * `en` stays static, and that is not an oversight. It is `fallbackLng`, so it is needed on
 * every render whatever the active locale; and it is what the first paint uses while a
 * lazy bundle is in flight. Loading it lazily too would buy nothing and add a request to
 * the critical path.
 * ────────────────────────────────────────────────────────────────────────────
 */
import { LOCALES, type Locale } from '@lila/shared';
import i18next, { type i18n as I18n } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { resources as en } from './locales/en';

/** One resource bundle: namespace -> nested messages. */
export type LocaleResources = typeof en;

/*
 * A literal map of `import()` calls, not a computed path. Vite needs the specifier to be
 * statically analysable to emit a chunk for it; `import(`./locales/${locale}`)` compiles,
 * and then ships all four again as a glob.
 */
const LOADERS: Record<Locale, () => Promise<{ resources: LocaleResources }>> = {
  en: () => Promise.resolve({ resources: en }),
  ru: () => import('./locales/ru'),
  uk: () => import('./locales/uk'),
  de: () => import('./locales/de'),
};

/** Locales whose bundle has been added to the instance, so a switch back is free. */
const loaded = new Set<Locale>(['en']);

/*
 * `billing` and `admin` were added in phase 5, `landing` with the marketing home page.
 * Adding a namespace means editing this list, the four `lib/locales/*.ts` barrels, and four
 * JSON files — and `test/i18n.test.ts` checks every namespace in this list for key parity,
 * so a locale that forgets one fails.
 */
export const NAMESPACES = [
  'common',
  'board',
  'studio',
  'errors',
  'ai',
  'billing',
  'admin',
  'landing',
  'pages',
  'profile',
  'sessions',
] as const;

const STORAGE_KEY = 'lila.locale';

export function isLocale(value: string | undefined): value is Locale {
  return value !== undefined && (LOCALES as readonly string[]).includes(value);
}

/** Spec 4.3 puts the locale preference in localStorage. No tokens ever go there. */
export function storedLocale(): Locale | null {
  try {
    const value = globalThis.localStorage.getItem(STORAGE_KEY);
    return isLocale(value ?? undefined) ? (value as Locale) : null;
  } catch {
    /*
     * Not a presence check — the global always exists. Safari in private browsing and a
     * browser with site data blocked both *throw* on access, and a language preference is
     * never worth failing a page render over.
     */
    return null;
  }
}

export function storeLocale(locale: Locale): void {
  try {
    globalThis.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* See storedLocale. */
  }
}

/** Resolution order: explicit path → stored preference → browser → English. */
export function resolveLocale(pathLocale?: string): Locale {
  if (isLocale(pathLocale)) return pathLocale;

  const stored = storedLocale();
  if (stored !== null) return stored;

  for (const language of globalThis.navigator.languages) {
    const base = language.split('-')[0];
    if (isLocale(base)) return base;
  }

  return 'en';
}

export function createI18n(locale: Locale): I18n {
  const instance = i18next.createInstance();

  /*
   * Initialised synchronously with English only. `init` is not awaited and never was —
   * with in-memory resources it completes in the same tick, and React needs an instance to
   * render against immediately. The active locale's own bundle arrives through
   * `loadLocale` below, which the `/$lang` route awaits before it renders.
   */
  void instance.use(initReactI18next).init({
    lng: locale,
    fallbackLng: 'en',
    ns: [...NAMESPACES],
    defaultNS: 'common',
    resources: { en },
    interpolation: {
      /* React escapes for us; doing it twice mangles apostrophes in French-style copy. */
      escapeValue: false,
    },
    returnNull: false,
  });

  return instance;
}

export const i18n = createI18n(resolveLocale());

/**
 * Fetches a locale's bundle and adds it to the instance. Idempotent and cached.
 *
 * A failure here is deliberately not fatal: `fallbackLng: 'en'` means the screen renders in
 * English rather than in raw message keys, which is a bad experience and not a broken one.
 * Throwing would turn a dropped chunk request on a train into a white page.
 */
export async function loadLocale(locale: Locale): Promise<void> {
  if (loaded.has(locale)) return;

  try {
    const { resources } = await LOADERS[locale]();
    for (const namespace of NAMESPACES) {
      i18n.addResourceBundle(locale, namespace, resources[namespace], true, true);
    }
    loaded.add(locale);
  } catch (error) {
    console.error(`Failed to load the ${locale} locale; falling back to English.`, error);
  }
}

export async function changeLocale(locale: Locale): Promise<void> {
  await loadLocale(locale);
  await i18n.changeLanguage(locale);
  storeLocale(locale);
  /* Assistive technology and the browser's own hyphenation both read this attribute. */
  globalThis.document.documentElement.lang = locale;
}
